const { APP: Types, AUTH, DATA_FETCHING, SURVEYS: SurveyTypes } = require('../action-types');
const { USERS_SORT_TYPE, SURVEYS_SORT_TYPE, DEFAULT_ROLE_GROUP_FILTERS } = require('../utils/constants');
const Storage = require('../utils/storage');
const PersistenceConfig = require('../utils/persistence-config');

const { keys: PersistenceKeys } = PersistenceConfig;

const now = Date.now();

const initialState = {
    mainAppLoading: true,
    isSwitchingToSchool: false,
    // Criteria and filters
    roleGroupFilter: {
        ...DEFAULT_ROLE_GROUP_FILTERS,
        ...Storage.get(PersistenceKeys.app.roleGroupFilter),
        discover: 'all'
    },
    notifySearchCriteria: {},
    surveySearchCriteria: {},
    surveysSortType: SURVEYS_SORT_TYPE.BY_DATE_UPDATED,
    surveysManagementFilter: 'active',
    dmsFilter: '',
    discoverSortType: USERS_SORT_TYPE.BEST_MATCH,
    discoverSearchFilter: {},
    discoverNavigation: {
        currentResultIndex: null,
        fromDiscover: false
    },
    groupNavigation: {
        currentResultIndex: null,
        fromGroup: false,
        groupId: null
    },
    shouldRefreshSearch: { discover: true, group: true },
    refreshSearchTimestamps: { discover: now, group: now },
    interestsSearchText: '',
    appMessages: {},
    //
    rememberedScroll: { discover: null, group: null },
    logoutDialogOpen: false,
    userReviewAvailableStatus: false,
    modalOpen: false,
    notificationsOpen: false,
    hasPrevPage: false,
    historyPathname: '/',
    historyPreviousPathname: '/',
    isDowntimeMode: false,
    downtimeMessage: null,
    downtimeType: null,
    inBackground: false,
    debug: Storage.get(PersistenceKeys.app.debug) || {
        discoverMatch: false,
        myGroupsPage: false,
        chat: false
    }
};

const internals = {};

const reducer = (state = initialState, action) => {

    switch (action.type) {
        // Reset app state on logout
        case AUTH.LOGOUT.SUCCESS:
        case AUTH.CLOSE_ACCOUNT.SUCCESS:
        case AUTH.ACCOUNT_CLOSED_CLEAR:
        case AUTH.LOGOUT_SYNC:
            Storage.set(PersistenceKeys.app.roleGroupFilter, DEFAULT_ROLE_GROUP_FILTERS);
            return {
                ...initialState,
                debug: { ...initialState.debug, appDiagnostics: state.debug.appDiagnostics === true }
            };
        case Types.SET_MAIN_APP_LOADING:
            return {
                ...state,
                mainAppLoading: action.payload.appLoading
            };
        case Types.SET_SCHOOL_SWITCHING_STATE:
            return {
                ...state,
                switchingToSchool: action.payload.switchingToSchool
            };
        case Types.SET_USER_REVIEW_STATUS:
            return {
                ...state,
                userReviewAvailableStatus: !!action.payload?.status
            };
        case Types.UPDATE_ROLE_GROUP_FILTER: {

            const newVal = {
                ...state.roleGroupFilter,
                ...action.payload
            };

            Storage.set(PersistenceKeys.app.roleGroupFilter, {
                ...newVal,
                discover: 'all'
            });

            return {
                ...state,
                roleGroupFilter: newVal
            };
        }

        case Types.UPDATE_DISCOVER_SORT_TYPE:
            return {
                ...state,
                discoverSortType: action.payload.type
            };
        case Types.UPDATE_SURVEYS_MANAGEMENT_FILTER:
            return {
                ...state,
                surveysManagementFilter: action.payload.filter
            };
        case Types.UPDATE_CONNECTIONS_FILTER:
            return {
                ...state,
                dmsFilter: action.payload.filter
            };
        case Types.UPDATE_CLASSMATE_DISCOVER_SEARCH_FILTER:
            return {
                ...state,
                discoverSearchFilter: action.payload.filter
            };
        case Types.UPDATE_NOTIFY_SEARCH_FILTER:
            return {
                ...state,
                notifySearchCriteria: action.payload.filter
            };
        case Types.UPDATE_SURVEY_SEARCH_FILTER:
            return {
                ...state,
                surveySearchCriteria: action.payload.filter
            };
        case Types.SET_FIREBASE_TOKEN:
            return {
                ...state,
                firebaseToken: action.payload.token
            };
        case Types.SET_IN_BACKGROUND:
            return {
                ...state,
                inBackground: action.payload.inBackground
            };
        case Types.SET_INTERESTS_SEARCH_TEXT:
            return {
                ...state,
                interestsSearchText: action.payload.interestsSearchText
            };
        case Types.SET_APP_MESSAGE:
            return {
                ...state,
                appMessages: {
                    ...state.appMessages,
                    [action.payload.key]: {
                        title: action.payload.title,
                        message: action.payload.message,
                        createdAt: Date.now()
                    }
                }
            };
        case Types.CLEAR_APP_MESSAGE: {
            const appMessages = {
                ...state.appMessages
            };

            delete appMessages[action.payload.key];

            return {
                ...state,
                appMessages
            };
        }
        case Types.OPEN_NOTIFICATIONS:
            return {
                ...state,
                notificationsOpen: true
            };
        case Types.CLOSE_NOTIFICATIONS:
            return {
                ...state,
                notificationsOpen: false
            };
        case Types.OPEN_LOGOUT_DIALOG:
            return {
                ...state,
                logoutDialogOpen: true
            };
        case Types.CLOSE_LOGOUT_DIALOG:
            return {
                ...state,
                logoutDialogOpen: false
            };
        case Types.OPEN_MODAL:
            return {
                ...state,
                modalOpen: true
            };
        case Types.CLOSE_MODAL:
            return {
                ...state,
                modalOpen: false
            };
        case Types.SET_HISTORY_PATHNAME:
            return {
                ...state,
                historyPathname: action.payload.pathname
            };
        case Types.SET_HISTORY_PREVIOUS_PATHNAME:
            return {
                ...state,
                historyPreviousPathname: action.payload.pathname
            };
        case Types.REMEMBER_SCROLL:
            return {
                ...state,
                rememberedScroll: {
                    ...state.rememberedScroll,
                    ...action.payload.type
                }
            };
        case Types.RESTORE_SCROLL:
            return {
                ...state,
                rememberedScroll: {
                    ...state.rememberedScroll,
                    [action.payload.type]: null
                }
            };
        case Types.SHOULD_REFRESH_SEARCH:
            return {
                ...state,
                shouldRefreshSearch: {
                    ...state.shouldRefreshSearch,
                    ...action.payload
                },
                ...internals.mergeIf(action.payload.discover && action.payload.group, {
                    rememberedScroll: { discover: null, group: null }
                })
            };
        case Types.IS_DOWNTIME_MODE:
            return {
                ...state,
                isDowntimeMode: action.payload.value,
                downtimeMessage: action.payload.value ? action.payload.message : null,
                downtimeType: action.payload.value ? action.payload.type || 'maintenance' : null
            };
        case Types.IS_FORCE_APP_UPGRADE_MODE:
            return {
                ...state,
                isForceAppUpgradeMode: action.payload.value,
                apiAppVersion: action.payload.apiAppVersion,
                frontendAppVersion: action.payload.frontendAppVersion
            };
        case Types.SET_DEBUG: {

            const newVal = {
                ...state.debug,
                ...action.payload.debug
            };

            Storage.set(PersistenceKeys.app.debug, newVal);

            return {
                ...state,
                debug: newVal
            };
        }
        case Types.SET_DISCOVER_NAVIGATION:
            return {
                ...state,
                discoverNavigation: {
                    currentResultIndex: action.payload.currentResultIndex,
                    fromDiscover: action.payload.fromDiscover
                }
            };
        case Types.CLEAR_DISCOVER_NAVIGATION:
            return {
                ...state,
                discoverNavigation: {
                    currentResultIndex: null,
                    fromDiscover: false
                }
            };
        case Types.SET_GROUP_NAVIGATION:
            return {
                ...state,
                groupNavigation: {
                    currentResultIndex: action.payload.currentResultIndex,
                    fromGroup: action.payload.fromGroup,
                    groupId: action.payload.groupId
                }
            };
        case Types.CLEAR_GROUP_NAVIGATION:
            return {
                ...state,
                groupNavigation: {
                    currentResultIndex: null,
                    fromGroup: false,
                    groupId: null
                }
            };
        case Types.CLEAR_VALUES_FOR_SCHOOL_SWITCH:
            return {
                ...state,
                discoverSortType: USERS_SORT_TYPE.BEST_MATCH,
                roleGroupFilter: DEFAULT_ROLE_GROUP_FILTERS,
                discoverSearchFilter: {}
            };
        // Other action types
        case DATA_FETCHING.FETCH_CLASS_USER_SEARCH_RESULTS.FIRST_PAGE.REQUEST:
            return {
                ...state,
                rememberedScroll: {
                    ...state.rememberedScroll,
                    group: null
                },
                refreshSearchTimestamps: {
                    ...state.refreshSearchTimestamps,
                    group: Date.now()
                }
            };
        case DATA_FETCHING.FETCH_USER_SEARCH_RESULTS.FIRST_PAGE.REQUEST:
            return {
                ...state,
                rememberedScroll: {
                    ...state.rememberedScroll,
                    discover: null
                },
                refreshSearchTimestamps: {
                    ...state.refreshSearchTimestamps,
                    discover: Date.now()
                }
            };
        case DATA_FETCHING.FETCH_MESSAGES.HAS_PREV_PAGE.SUCCESS:
            return {
                ...state,
                hasPrevPage: action.payload.result.result
            };
        case SurveyTypes.SET_SORT_TYPE:
            return {
                ...state,
                surveysSortType: action.payload.sortType
            };
        default:
            return state;
    }
};

const selectors = {
    getMainAppLoading: (state) => state.mainAppLoading,
    getSwitchingSchoolText: (state) => state.switchingToSchool,
    getFirebaseToken: (state) => state.firebaseToken,
    getDiscoverSortType: (state) => state.discoverSortType,
    getNotificationsOpen: (state) => !!state.notificationsOpen,
    getUserReviewAvailableStatus: (state) => !!state.userReviewAvailableStatus,
    getLogoutDialogOpen: (state) => !!state.logoutDialogOpen,
    getIsAppInBackground: (state) => state.inBackground,
    getModalIsOpen: (state) => state.modalOpen,
    getDMsFilter: (state) => state.dmsFilter,
    getDiscoverSearchFilter: (state) => state.discoverSearchFilter,
    getNotifySearchCriteria: (state) => state.notifySearchCriteria,
    getSurveySearchCriteria: (state) => state.surveySearchCriteria,
    getSurveysManagementFilter: (state) => state.surveysManagementFilter,
    getInterestsSearchText: (state) => state.interestsSearchText,
    getAppMessage: (state, key) => state.appMessages?.[key] || null,
    getHasPrevPage: (state) => state.hasPrevPage,
    getIsDowntimeMode: (state) => state.isDowntimeMode,
    getDowntimeMessage: (state) => state.downtimeMessage,
    getDowntimeType: (state) => state.downtimeType,
    getIsForceAppUpgradeMode: (state) => state.isForceAppUpgradeMode,
    getApiAppVersion: (state) => state.apiAppVersion,
    getFrontendAppVersion: (state) => state.frontendAppVersion,
    getSurveysSortType: (state) => state.surveysSortType,
    getDiscoverNavigation: (state) => state.discoverNavigation,
    getDiscoverNavigationFromDiscover: (state) => state.discoverNavigation.fromDiscover,
    getDiscoverNavigationCurrentIndex: (state) => state.discoverNavigation.currentResultIndex,
    getRoleGroupFilter: (state, type) => state.roleGroupFilter[type] || 'all',
    getRoleGroupFilter_ForGroup: (state, groupId) => {

        const { context } = require('../app-context');

        const allSelectors = context.selectors.all;
        const redux = context.redux.hooks;

        const fullState = redux.getState();

        const availableRoleGroups = allSelectors.getUsedRoleGroups_byGroupId(fullState, groupId);
        const roleGroupFilterName = selectors.getRoleGroupFilter(state, 'groups');

        const roleGroupFilter = availableRoleGroups.find(({ name }) => name === roleGroupFilterName);

        return roleGroupFilter || 'all';
    },
    getGroupNavigation: (state) => state.groupNavigation,
    getGroupNavigationFromGroup: (state) => state.groupNavigation.fromGroup,
    getGroupNavigationCurrentIndex: (state) => state.groupNavigation.currentResultIndex,
    getGroupNavigationGroupId: (state) => state.groupNavigation.groupId,
    getDebug: (state) => state.debug
};

module.exports = Object.assign(reducer, { selectors });

internals.mergeIf = (condition, obj) => {

    if (condition) {
        return obj;
    }

    return {};
};
